/**
 * Diagramme de Venn — géométrie pure : cercles, nom des ensembles, places des
 * éléments dans chaque région (loin des traits et des noms).
 */

export const VW = 320;
/** hauteur de la figure : plus haute à trois ensembles */
export const vh = (n: 2 | 3) => (n === 2 ? 220 : 300);
export const rect = (n: 2 | 3) => ({ x: 6, y: 6, w: VW - 12, h: vh(n) - 12 });

export interface Circle {
  x: number;
  y: number;
  r: number;
  /** position du nom de l'ensemble */
  lx: number;
  ly: number;
}

export function circles(n: 2 | 3): Circle[] {
  if (n === 2)
    return [
      { x: 122, y: 112, r: 80, lx: 58, ly: 44 },
      { x: 198, y: 112, r: 80, lx: 262, ly: 44 },
    ];
  return [
    { x: 116, y: 106, r: 90, lx: 44, ly: 30 },
    { x: 204, y: 106, r: 90, lx: 276, ly: 30 },
    { x: 160, y: 184, r: 90, lx: 52, ly: 270 },
  ];
}

export interface Slot {
  x: number;
  y: number;
}

/** marge aux traits (px) */
/** écart minimal entre deux éléments : 24 px côte à côte, 17 px l'un sous l'autre */
const GAP_X = 24;
const GAP_Y = 17;
const MARGIN = 11;
const FINE = 4;

/**
 * Places libres de chaque région (masque d'appartenance aux cercles) : on part
 * du point le plus éloigné des bords de la région, puis on ajoute, du plus
 * proche au plus lointain, les points à au moins GAP px des places déjà prises.
 */
export function slots(n: 2 | 3): Map<number, Slot[]> {
  const cs = circles(n);
  const R = rect(n);
  const cands = new Map<number, Array<Slot & { clear: number }>>();
  for (let y = R.y + 12; y <= R.y + R.h - 8; y += FINE)
    for (let x = R.x + 12; x <= R.x + R.w - 10; x += FINE) {
      const toLine = Math.min(...cs.map((c) => Math.abs(Math.hypot(x - c.x, y - c.y) - c.r)));
      const nearLabel = cs.some((c) => Math.hypot(x - c.lx, y - c.ly) < 26);
      const nearE = x < 40 && y < 40;
      if (toLine < MARGIN || nearLabel || nearE) continue;
      const mask = cs.reduce(
        (m, c, i) => (Math.hypot(x - c.x, y - c.y) < c.r ? m | (1 << i) : m),
        0,
      );
      const toEdge = Math.min(x - R.x, R.x + R.w - x, y - R.y, R.y + R.h - y);
      const list = cands.get(mask) ?? [];
      list.push({ x, y, clear: Math.min(toLine, toEdge) });
      cands.set(mask, list);
    }
  const out = new Map<number, Slot[]>();
  for (const [mask, list] of cands) {
    const center = list.reduce((b, p) => (p.clear > b.clear ? p : b));
    const chosen: Slot[] = [];
    // une région étroite (deux cercles sur trois) se remplit un peu plus serré
    const tight = n === 3 && (mask === 3 || mask === 5 || mask === 6);
    const gx = tight ? 21 : GAP_X;
    const gy = tight ? 15 : GAP_Y;
    const far = (p: Slot) =>
      Math.min(...chosen.map((q) => Math.hypot((p.x - q.x) / gx, (p.y - q.y) / gy)));
    if (mask === 0) {
      // hors des cercles : éléments répartis dans les coins (le plus loin des précédents)
      chosen.push({ x: center.x, y: center.y });
      while (chosen.length < 14) {
        const best = list.reduce((b, p) => (far(p) > far(b) ? p : b));
        if (far(best) < 1) break;
        chosen.push({ x: best.x, y: best.y });
      }
    } else {
      // dans une région : groupés autour de son cœur
      const dist = (p: Slot) => Math.hypot(p.x - center.x, p.y - center.y);
      for (const p of [...list].sort((a, b) => dist(a) - dist(b))) {
        if (chosen.length === 0 || far(p) >= 1) chosen.push({ x: p.x, y: p.y });
        if (chosen.length >= 14) break;
      }
    }
    out.set(mask, chosen);
  }
  return out;
}
